import type { PaymentKind, ProofRole } from "./enums";

// deposit-proof — THE MONEY LINES, ONE RULE FOR BOTH SIDES OF THE GLASS.
//
// The desk's Money card, the customer's pay sheet and the upload endpoints all
// ask the same four questions of a booking's ledger and its screenshots: what
// is still due on each line, which line a screenshot is for, is one already
// waiting on Patricia, and what colour the card wears. Answered here once, so
// the card can never say *Paid* while the sheet still asks (AC9a, AC10a, AC12,
// AC18, AC19, AC19c). The web imports it; the API imports it; neither copies.

/** A payment proof as every reader sees it — one row per attempt, nothing replaced. */
export interface PaymentProof {
  id: string;
  kind: PaymentKind;
  createdAt: string;
  uploadedBy: string;
  uploadedRole: ProofRole;
  mime: string;
  verifiedAt: string | null;
  rejectedAt: string | null;
  /** The line Patricia was given to send when she said no. */
  rejectedReason: string | null;
  /** The payment this screenshot produced, or was linked to (AC12a). */
  paymentId: string | null;
}

export type MoneyLineState = "due" | "pending" | "paid" | "refused";

export interface MoneyLine {
  kind: PaymentKind;
  /** What this line asks for: the deposit quoted, or the rest of the stay. */
  asked: number;
  /** What the live ledger holds against this kind. */
  paid: number;
  /** What is still to come — the deposit's own shortfall, or the whole outstanding total. */
  left: number;
  state: MoneyLineState;
  /** The screenshot the state is about: the one waiting, or the one refused. */
  proof: PaymentProof | null;
}

export interface MoneyLedger {
  total: number;
  /** The deposit the customer was asked for before the cap at the total. */
  depositAsked: number;
  /** Every payment row, voided ones too — a voided row is how a screenshot loses its credit (#37). */
  payments: readonly { kind: PaymentKind; amount: number; voidedAt?: string | null; proofId?: string | null }[];
  /** Every payment proof on the booking, any order — card proofs are ignored. */
  proofs: readonly PaymentProof[];
}

const round = (n: number) => Math.round(n * 100) / 100;
const newestFirst = (a: PaymentProof, b: PaymentProof) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0);
const noVerdict = (p: PaymentProof) => p.verifiedAt === null && p.rejectedAt === null;
const isMoney = (p: PaymentProof) => p.kind === "deposit" || p.kind === "remainder";

export function moneyLines(l: MoneyLedger): { deposit: MoneyLine; remainder: MoneyLine } {
  const total = Math.max(0, l.total);
  const live = l.payments.filter((p) => !p.voidedAt);
  const paidOf = (kind: PaymentKind) => round(live.filter((p) => p.kind === kind).reduce((s, p) => s + p.amount, 0));
  const paidAll = round(live.reduce((s, p) => s + p.amount, 0));
  const depositAsked = round(Math.min(Math.max(0, l.depositAsked), total));
  const proofs = l.proofs.filter(isMoney).slice().sort(newestFirst);
  // A SCREENSHOT IS CREDITED while it was accepted and the money it recorded still stands: a
  // payment linked to it and voided since strips its credit (PR #255 review, #37). One accepted
  // with no payment of its own (the desk had recorded the line) keeps it.
  const credited = (p: PaymentProof) => {
    if (p.verifiedAt === null) return false;
    const linked = l.payments.filter((pay) => pay.proofId === p.id);
    return linked.length === 0 || linked.some((pay) => !pay.voidedAt);
  };
  const line = (kind: PaymentKind, asked: number, paid: number, left: number): MoneyLine => {
    const mine = proofs.filter((p) => p.kind === kind);
    const pending = mine.find(noVerdict) ?? null;
    if (pending) return { kind, asked, paid, left, state: "pending", proof: pending };
    const accepted = mine.find(credited) ?? null;
    // A covered line keeps the screenshot that covered it — the newest accepted
    // one — so the desk can always open it (Freddi, 2026-10-11); covered by hand, none.
    if (left <= 0) return { kind, asked, paid, left: 0, state: "paid", proof: accepted };
    const newest = mine[0] ?? null;
    if (newest && newest.rejectedAt !== null) return { kind, asked, paid, left, state: "refused", proof: newest };
    // A part-paid line keeps the screenshot accepted for its part (PR #255 review, #33).
    return { kind, asked, paid, left, state: "due", proof: accepted };
  };
  // MONEY FILLS THE DEPOSIT FIRST, WHATEVER IT IS TAGGED (PR #256 review): the desk's Settle and
  // migration 0018 record desk money as `remainder`, so a stay settled at the desk read "deposit
  // due" once desk bookings opened the card. The customer's Home already reads it this way.
  const depositPaid = round(Math.max(paidOf("deposit"), Math.min(paidAll, depositAsked)));
  return {
    deposit: line("deposit", depositAsked, depositPaid, round(Math.max(0, depositAsked - depositPaid))),
    remainder: line("remainder", round(Math.max(0, total - depositAsked)), paidOf("remainder"), round(Math.max(0, total - paidAll))),
  };
}

/** Which line a new screenshot is for: the deposit while any of it is left, then the remainder, then nothing (AC12, AC19). */
export function proofKindDue(lines: { deposit: MoneyLine; remainder: MoneyLine }): PaymentKind | null {
  // The stay settled in full, however it was paid, asks for nothing (PR #255 review, #32).
  if (lines.remainder.left <= 0) return null;
  if (lines.deposit.left > 0) return "deposit";
  if (lines.remainder.left > 0) return "remainder";
  return null;
}

/** The newest payment proof with no verdict, of either kind — one in flight per booking (AC18). */
export function pendingProof(proofs: readonly PaymentProof[]): PaymentProof | null {
  return proofs.filter(isMoney).filter(noVerdict).sort(newestFirst)[0] ?? null;
}

/** G24's grounds: blue waits on Patricia, gold on the customer, green is live, grey is finished (AC9a). */
export type MoneyTone = "blue" | "gold" | "green" | "grey";
export function lineTone(state: MoneyLineState): MoneyTone {
  switch (state) {
    case "pending": return "blue";
    case "due": return "gold";
    case "paid": return "green";
    case "refused": return "grey";
  }
}

// The codes the sheet and the card both read — the story's own words.
export const PROOF_PENDING_CODE = "proof_pending";
export const NOTHING_DUE_CODE = "nothing_due";
export const ALREADY_DECIDED_CODE = "already_decided";

// --- the IBAN (AC16a, AC16b) ------------------------------------------------
//
// A typo with a Copy button beside it is money sent to the wrong account, so
// the check is the real one: the country's length and the mod-97 remainder,
// never a shape alone. The length table is the SEPA area plus the few the
// desk is likely to meet; a country not in it is checked by the remainder
// alone rather than refused for being foreign.

const IBAN_LENGTH: Record<string, number> = {
  AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24, FI: 18, FR: 27,
  GB: 22, GR: 27, HR: 21, HU: 28, IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20, LV: 21, MT: 31,
  NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24,
};

/** Upper case, no spaces — the shape it is stored in. */
export function normaliseIban(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

export function ibanValid(raw: string): boolean {
  const iban = normaliseIban(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  // The length is checked where we know it; an unlisted country rests on shape and mod-97 (PR #255 review).
  const want = IBAN_LENGTH[iban.slice(0, 2)];
  if (want !== undefined && iban.length !== want) return false;
  // Move the country and check digits to the end, letters to numbers, mod 97 in chunks.
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  let rem = 0;
  for (let i = 0; i < digits.length; i += 7) rem = Number(String(rem) + digits.slice(i, i + 7)) % 97;
  return rem === 1;
}

/** Groups of four, the way a bank prints it. */
export function formatIban(iban: string): string {
  return normaliseIban(iban).replace(/(.{4})/g, "$1 ").trim();
}
